/**
 * Show a country flag next to customer name using country_code (preferred) or IANA timezone.
 * Timezone → country is approximate (many zones map to one country; some are ambiguous).
 */

/** Common IANA zones → ISO 3166-1 alpha-2 (extend as needed). */
const IANA_TO_ISO2 = {
  "Africa/Cairo": "EG",
  "Africa/Casablanca": "MA",
  "Africa/Johannesburg": "ZA",
  "Africa/Lagos": "NG",
  "Africa/Nairobi": "KE",
  "America/Anchorage": "US",
  "America/Argentina/Buenos_Aires": "AR",
  "America/Bogota": "CO",
  "America/Caracas": "VE",
  "America/Chicago": "US",
  "America/Chihuahua": "MX",
  "America/Denver": "US",
  "America/Edmonton": "CA",
  "America/Guatemala": "GT",
  "America/Halifax": "CA",
  "America/Lima": "PE",
  "America/Los_Angeles": "US",
  "America/Mexico_City": "MX",
  "America/Montevideo": "UY",
  "America/New_York": "US",
  "America/Phoenix": "US",
  "America/Santiago": "CL",
  "America/Sao_Paulo": "BR",
  "America/St_Johns": "CA",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "Asia/Amman": "JO",
  "Asia/Baghdad": "IQ",
  "Asia/Bahrain": "BH",
  "Asia/Baku": "AZ",
  "Asia/Bangkok": "TH",
  "Asia/Beirut": "LB",
  "Asia/Colombo": "LK",
  "Asia/Dhaka": "BD",
  "Asia/Dubai": "AE",
  "Asia/Doha": "QA",
  "Asia/Ho_Chi_Minh": "VN",
  "Asia/Hong_Kong": "HK",
  "Asia/Jakarta": "ID",
  "Asia/Jerusalem": "IL",
  "Asia/Karachi": "PK",
  "Asia/Kathmandu": "NP",
  "Asia/Kolkata": "IN",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Kuwait": "KW",
  "Asia/Manila": "PH",
  "Asia/Muscat": "OM",
  "Asia/Nicosia": "CY",
  "Asia/Qatar": "QA",
  "Asia/Riyadh": "SA",
  "Asia/Seoul": "KR",
  "Asia/Shanghai": "CN",
  "Asia/Singapore": "SG",
  "Asia/Taipei": "TW",
  "Asia/Tehran": "IR",
  "Asia/Tokyo": "JP",
  "Asia/Yangon": "MM",
  "Asia/Yerevan": "AM",
  "Australia/Adelaide": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Perth": "AU",
  "Australia/Sydney": "AU",
  "Europe/Amsterdam": "NL",
  "Europe/Athens": "GR",
  "Europe/Belgrade": "RS",
  "Europe/Berlin": "DE",
  "Europe/Brussels": "BE",
  "Europe/Bucharest": "RO",
  "Europe/Budapest": "HU",
  "Europe/Copenhagen": "DK",
  "Europe/Dublin": "IE",
  "Europe/Helsinki": "FI",
  "Europe/Istanbul": "TR",
  "Europe/Kiev": "UA",
  "Europe/Lisbon": "PT",
  "Europe/London": "GB",
  "Europe/Madrid": "ES",
  "Europe/Moscow": "RU",
  "Europe/Oslo": "NO",
  "Europe/Paris": "FR",
  "Europe/Prague": "CZ",
  "Europe/Rome": "IT",
  "Europe/Stockholm": "SE",
  "Europe/Vienna": "AT",
  "Europe/Warsaw": "PL",
  "Europe/Zurich": "CH",
  "Indian/Maldives": "MV",
  "Indian/Mauritius": "MU",
  "Pacific/Auckland": "NZ",
  "Pacific/Fiji": "FJ",
  "Pacific/Guam": "GU",
  "Pacific/Honolulu": "US",
  "UTC": "GB",
  "Etc/UTC": "GB",
  "GMT": "GB",
};

function normalizeIso2(raw) {
  if (raw == null || typeof raw !== "string") return null;
  const s = raw.trim().toUpperCase();
  if (s.length === 2 && /^[A-Z]{2}$/.test(s)) return s;
  return null;
}

function timezoneToIso2(tz) {
  if (!tz || typeof tz !== "string") return null;
  const key = tz.trim();
  if (!key) return null;
  if (IANA_TO_ISO2[key]) return IANA_TO_ISO2[key];
  const lower = key.toLowerCase();
  for (const [k, v] of Object.entries(IANA_TO_ISO2)) {
    if (k.toLowerCase() === lower) return v;
  }
  return null;
}

const ISO2_TO_COUNTRY_NAME = {
  AE: "United Arab Emirates",
  AM: "Armenia",
  AR: "Argentina",
  AT: "Austria",
  AU: "Australia",
  AZ: "Azerbaijan",
  BD: "Bangladesh",
  BE: "Belgium",
  BH: "Bahrain",
  BR: "Brazil",
  CA: "Canada",
  CH: "Switzerland",
  CL: "Chile",
  CN: "China",
  CO: "Colombia",
  CY: "Cyprus",
  CZ: "Czech Republic",
  DE: "Germany",
  DK: "Denmark",
  EG: "Egypt",
  ES: "Spain",
  FI: "Finland",
  FJ: "Fiji",
  FR: "France",
  GB: "United Kingdom",
  GR: "Greece",
  GT: "Guatemala",
  GU: "Guam",
  HK: "Hong Kong",
  HU: "Hungary",
  ID: "Indonesia",
  IE: "Ireland",
  IL: "Israel",
  IN: "India",
  IR: "Iran",
  IQ: "Iraq",
  IT: "Italy",
  JO: "Jordan",
  JP: "Japan",
  KE: "Kenya",
  KR: "South Korea",
  KW: "Kuwait",
  LB: "Lebanon",
  LK: "Sri Lanka",
  MA: "Morocco",
  MM: "Myanmar",
  MX: "Mexico",
  MY: "Malaysia",
  MV: "Maldives",
  MU: "Mauritius",
  NG: "Nigeria",
  NL: "Netherlands",
  NO: "Norway",
  NP: "Nepal",
  NZ: "New Zealand",
  OM: "Oman",
  PE: "Peru",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Poland",
  PT: "Portugal",
  QA: "Qatar",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russia",
  SA: "Saudi Arabia",
  SE: "Sweden",
  SG: "Singapore",
  TH: "Thailand",
  TR: "Turkey",
  TW: "Taiwan",
  UA: "Ukraine",
  US: "United States",
  UY: "Uruguay",
  VE: "Venezuela",
  VN: "Vietnam",
  ZA: "South Africa",
};

/**
 * Prefer explicit country; fall back to timezone-inferred country/region.
 * @returns {{ country: string|null, timezone: string|null, source: 'country'|'timezone'|null }}
 */
export function resolveCustomerLocation(customer) {
  const country =
    String(customer?.country_full_name || "").trim() ||
    String(customer?.country || "").trim() ||
    null;
  const timezone = String(customer?.timezone || "").trim() || null;
  if (country) {
    return { country, timezone, source: "country" };
  }
  const iso2 = timezoneToIso2(timezone);
  if (iso2 && ISO2_TO_COUNTRY_NAME[iso2]) {
    return {
      country: ISO2_TO_COUNTRY_NAME[iso2],
      timezone,
      source: "timezone",
    };
  }
  if (timezone) {
    const parts = timezone.split("/");
    const leaf = parts[parts.length - 1]?.replace(/_/g, " ").trim();
    if (leaf) {
      return { country: leaf, timezone, source: "timezone" };
    }
  }
  return { country: null, timezone, source: null };
}

/** Unicode regional indicator symbols → flag emoji */
export function countryCodeToFlagEmoji(iso2) {
  const cc = normalizeIso2(iso2);
  if (!cc) return "";
  const A = 0x1f1e6;
  const cp = (ch) => A + (ch.charCodeAt(0) - 65);
  return String.fromCodePoint(cp(cc[0]), cp(cc[1]));
}

/**
 * @returns {{ emoji: string, title: string } | null}
 */
export function getCustomerFlagMeta(customer) {
  if (!customer) return null;
  const fromCode = normalizeIso2(customer.country_code);
  const fromTz = fromCode ? null : timezoneToIso2(customer.timezone);
  const iso2 = fromCode || fromTz;
  if (!iso2) return null;
  const emoji = countryCodeToFlagEmoji(iso2);
  if (!emoji) return null;
  const parts = [];
  if (customer.timezone) parts.push(customer.timezone);
  if (customer.country_full_name || customer.country) {
    parts.push(customer.country_full_name || customer.country);
  }
  parts.push(iso2);
  const title = parts.filter(Boolean).join(" · ");
  return { emoji, title };
}
